/**
 * SidebarLayout — left rail + content, with an optional full-width action bar.
 *
 * Why a component and not a nested route: in the legacy Vue app the rail is a
 * layout route (`/dashboard`) shared by five children, but here only Manage
 * Portfolio uses it so far. Composing it in the page keeps the generated route
 * table flat; if more sections need the rail it promotes to a nested route
 * without changing this component.
 *
 * The `actionBar` slot sits BELOW the rail-and-content row and spans the full
 * width of both — that is where a page's note/action bar goes. Putting it here
 * rather than inside the page's content column is what makes it run the whole
 * width instead of starting after the rail.
 *
 * NOT a page footer: the site footer (Company / News & Events / …) is separate
 * chrome that lives outside the app shell entirely.
 *
 * COLLAPSIBLE when the page passes `onOpenChange` (legacy SideBarComponent.vue):
 *  - a ‹ button at the rail's top-right closes it; a slim › strip at the left
 *    edge opens it again;
 *  - closed, the rail is HIDDEN, not unmounted — its selections and the loaded
 *    portfolio list survive, as with legacy's v-show;
 *  - it SLIDES open and shut (width animation; instant for anyone who has asked
 *    their system for reduced motion);
 *  - on small screens (< 768px, legacy's breakpoint) it starts closed, and it
 *    follows the screen only when the width CROSSES the breakpoint. Legacy
 *    re-forces it open on every resize above 768px, which undoes the user's
 *    own choice the moment the window moves by a pixel.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@components/common/icons';
import { cx } from '@lib/classNames';

import styles from './SidebarLayout.module.css';

/** Legacy: `window.innerWidth < 768`. */
const SMALL_SCREEN_QUERY = '(max-width: 767px)';

/** Length of the slide — keep in step with --rail-slide in the stylesheet. */
const SLIDE_MS = 220;

/** matchMedia is missing in some environments (old browsers, jsdom). */
function smallScreenQuery() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(SMALL_SCREEN_QUERY)
    : null;
}

export function SidebarLayout({
  sidebar,
  actionBar,
  open = true,
  onOpenChange,
  className,
  children,
}) {
  const railId = useId();
  const collapsible = typeof onOpenChange === 'function';
  const isOpen = collapsible ? open : true;

  /*
   * True only while the rail is sliding. The slide needs `overflow: hidden`
   * (the content must not spill out of a narrowing rail), but left on for good
   * it would clip every dropdown menu and tooltip inside the open rail — so it
   * is applied for the duration of the animation only.
   */
  const [sliding, setSliding] = useState(false);

  /*
   * Turn the clipping on IN THE SAME RENDER that flips open/closed — not in an
   * effect afterwards. An effect runs after the browser has painted, so on
   * opening there was one frame with the rail already visible but not yet
   * clipped: the whole sidebar flashed at full width, then slid. Setting state
   * during render (React's "adjust state on a prop change" pattern) makes React
   * redo the render before anything reaches the screen.
   */
  const [prevOpen, setPrevOpen] = useState(isOpen);
  if (prevOpen !== isOpen) {
    setPrevOpen(isOpen);
    setSliding(true);
  }

  // transitionend never fires when there is no transition (reduced motion,
  // small screens), and the clipping must not get stuck on — so it always
  // clears shortly after the slide's length, whatever happens.
  useEffect(() => {
    if (!sliding) return undefined;
    const timer = setTimeout(() => setSliding(false), SLIDE_MS + 100);
    return () => clearTimeout(timer);
  }, [sliding, isOpen]);

  // Small screens: start closed — before the first paint, so a phone never sees
  // the rail slide shut on load — then follow breakpoint CROSSINGS only.
  useLayoutEffect(() => {
    if (!collapsible) return undefined;
    const query = smallScreenQuery();
    if (!query) return undefined;

    if (query.matches) onOpenChange(false);
    const onCross = (e) => onOpenChange(!e.matches);
    query.addEventListener('change', onCross);
    return () => query.removeEventListener('change', onCross);
    // Run once per mount: re-running on every render would re-close the rail
    // after the user opened it on a phone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsible]);

  /*
   * The rail is sized to the screen MINUS the action bar beneath it, so the
   * rail and the bar both fit without the page scrolling. The bar's height is
   * not fixed — it stacks into two or three rows on narrow screens — so it is
   * measured, and kept up to date, as a CSS variable the stylesheet reads.
   */
  const layoutRef = useRef(null);
  const actionBarRef = useRef(null);
  useLayoutEffect(() => {
    const layout = layoutRef.current;
    const bar = actionBarRef.current;
    if (!layout) return undefined;

    const apply = () =>
      layout.style.setProperty('--sidebar-action-bar-height', `${bar ? bar.offsetHeight : 0}px`);
    apply();
    if (!bar) return undefined;

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(apply);
      observer.observe(bar);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [actionBar]);

  return (
    <div ref={layoutRef} className={cx(styles.layout, className)}>
      <div className={styles.row}>
        {collapsible && !isOpen ? (
          <button
            type="button"
            className={styles.expand}
            aria-label="Expand sidebar"
            aria-expanded="false"
            aria-controls={railId}
            onClick={() => onOpenChange(true)}
          >
            <ChevronRightIcon />
          </button>
        ) : null}

        {/*
          Closed is NOT `hidden` (display: none cannot be animated): the rail
          slides to zero width, then CSS makes it visibility: hidden. `inert`
          and aria-hidden take it out of the tab order and the accessibility
          tree straight away, even while it is still sliding out.
        */}
        <div
          id={railId}
          className={cx(styles.rail, !isOpen && styles.railClosed, sliding && styles.railSliding)}
          aria-hidden={isOpen ? undefined : 'true'}
          inert={isOpen ? undefined : ''}
          onTransitionEnd={(e) => {
            if (e.target === e.currentTarget && e.propertyName === 'width') setSliding(false);
          }}
        >
          {collapsible ? (
            <button
              type="button"
              className={styles.collapse}
              aria-label="Collapse sidebar"
              aria-expanded="true"
              aria-controls={railId}
              onClick={() => onOpenChange(false)}
            >
              <ChevronLeftIcon />
            </button>
          ) : null}
          {sidebar}
        </div>

        <div className={styles.content}>{children}</div>
      </div>

      {actionBar ? (
        <div ref={actionBarRef} className={styles.actionBar}>
          {actionBar}
        </div>
      ) : null}
    </div>
  );
}

export default SidebarLayout;
